import React, { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import {
    OrderMode,
    SupplyOrderState,
    useGetSupplyOrdersLazyQuery,
    useRelaunchPassSupplyOrdersToOrderedMutation,
    TotadminPermission,
} from 'data/__generated__';
import { formatDateAsAnniversary } from 'helpers/dateTimes';
import { MissingOdooSupplierPopup, getSuppliersMissingOdoo, OdooOrderSupplier } from './MissingOdooSupplierPopup';

export const RelaunchPassSupplyOrdersToOrderedPopup = ({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
}) => {
    const [relaunchPassSupplyOrdersToOrdered, { loading }] = useRelaunchPassSupplyOrdersToOrderedMutation();

    const [checkSupplyOrders, { loading: checking }] = useGetSupplyOrdersLazyQuery({ fetchPolicy: 'network-only' });
    const [missingSuppliers, setMissingSuppliers] = useState<OdooOrderSupplier[]>([]);

    async function handleSubmit() {
        if (loading || checking) return;
        const today = formatDateAsAnniversary({ dateTime: new Date() });
        try {
            const result = await checkSupplyOrders({ variables: { displayLimitDateFormatted: today } });
            if (result.error || !result.data) throw new Error('Supply orders unavailable');
            const missing = getSuppliersMissingOdoo(
                result.data.supplyOrders
                    .filter(
                        (order) =>
                            order.state === SupplyOrderState.Created &&
                            order.orderDate === today &&
                            order.supplier.orderMode === OrderMode.Email &&
                            order.supplier.shouldOrderAutomatically,
                    )
                    .map(({ supplier }) => supplier),
            );
            if (missing.length) {
                setMissingSuppliers(missing);
                return;
            }
        } catch {
            toast.error("Impossible de vérifier l'association Odoo des fournisseurs. Veuillez réessayer.");
            return;
        }
        const { data } = await relaunchPassSupplyOrdersToOrdered();

        const errorMessage = "Une erreur est survenue lors de passage automatique des commandes en 'Commandé'";
        if (!data) {
            toast.error(errorMessage);
            return;
        }

        const {
            relaunchPassSupplyOrdersToOrdered: { updatedSupplyOrders, errors },
        } = data;

        if (updatedSupplyOrders?.length) {
            toast.success(`Achat(s) commandé(s) : ${updatedSupplyOrders.length}`);
        } else if (!updatedSupplyOrders?.length && !errors?.length) {
            toast.warning('Algorithme executé sans erreur, aucun achat commandé');
        }

        if (errors?.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        setIsOpen(false);
    }

    if (missingSuppliers.length) {
        return (
            <MissingOdooSupplierPopup
                suppliers={missingSuppliers}
                isOpen={isOpen}
                setIsOpen={(open) => {
                    setIsOpen(open);
                    if (!open) setMissingSuppliers([]);
                }}
            />
        );
    }

    return (
        <TotemPopup title="Êtes-vous sûr.e ?" isOpen={isOpen} setIsOpen={setIsOpen}>
            Relancer le passage automatique en 'Commandé' des commandes du jour ? (Cela est normalement fait
            automatiquement tous les jours à 9h30).
            <SubmitContainer>
                <TotemPrimaryButton
                    onClick={handleSubmit}
                    disabled={loading || checking}
                    requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                >
                    {loading || checking ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Confirmer'}
                </TotemPrimaryButton>
            </SubmitContainer>
        </TotemPopup>
    );
};

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;
